import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { SecondaryButton } from "src/ui/shared/CustomButton";
import { useStreamingLog } from "src/ui/utils/useStream";

import { LogStreamModal, StreamStatus } from "@shared/LogStreamModal";

import { getReasonMessage, onDownloadLog, onStartStreaming } from "./LogStreamer.utils";

interface Props {
    buildId: number;
    hasLogs: boolean;
    disabled?: boolean;
}

export const LogStreamer = (props: Props): React.JSX.Element => {
    const [logStreamerOpen, setLogStreamerOpen] = React.useState(false);
    const [downloading, setDownloading] = React.useState(false);
    const [status, setStatus] = React.useState<StreamStatus>("initializing");

    const [params] = useSearchParams();
    const logParam = params.get("log");

    const { t } = useTranslation(["modals", "common"]);

    const { containerRef, scrollContainerRef, onClearStream, onRunStream } = useStreamingLog({
        onStartStreaming: () => onStartStreaming(props.buildId),
        onStatusChange: setStatus,
    });

    const downloadLog = async () => {
        setDownloading(true);
        await onDownloadLog(props.buildId);
        setDownloading(false);
    };

    const onOpen = () => {
        setLogStreamerOpen(true);
        onRunStream();
    };

    const onClose = () => {
        setLogStreamerOpen(false);
        onClearStream();
    };

    React.useLayoutEffect(() => {
        if (logParam === String(props.buildId)) {
            onOpen();
        }
    }, [props.buildId, logParam]);

    return (
        <React.Fragment>
            <SecondaryButton
                onClick={onOpen}
                disabled={!props.hasLogs || props.disabled}
                tooltipProps={{ title: getReasonMessage(props.hasLogs) }}
            >
                <i className="fa-solid fa-align-left" />
            </SecondaryButton>

            <LogStreamModal
                status={status}
                downloadDisabled={!props.hasLogs}
                open={logStreamerOpen}
                onClose={onClose}
                title={t("modals:logStreamer.buildLogsTitle", { id: props.buildId })}
                onDownload={downloadLog}
                downloading={downloading}
                containerRef={scrollContainerRef}
                contentRef={containerRef}
            />
        </React.Fragment>
    );
};
